import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IProfileService } from '@intigral/domain/lib/application/services/ProfileService';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { PARAMS } from '../../config/constants';
import h from '../../helpers/common';
import user from '../../helpers/user';
import { useProfileSwitching } from '../../hooks/useProfileSwitching';
import type { IProfileData } from '../../types/ILocalStorage';
import { getUserInfo } from '../../utils/user';
import { getProfileListFromCache, refreshProfileListQuery } from '../../utils/profileListQuery';
import { initialState } from '../defaultState';
import { STORE_NAME, apiPaths } from './../../config/constants';
import { fetchUserProfileMiniEPGChannelsList } from './miniEpgSlice';
import { fetchUserRevision } from './userRevisionSlice';
import { set } from '@intigral/utils/lib/browser-storage/localStorage';
import { toast } from '@intigral/shared';
import { __ } from '../../translations';
import { redirectToSelectProfileOnSwitchError } from '../../utils/profileSwitchRedirect';

interface LoadProfileParams {
    params: any;
    navigation?: (route: string) => void;
}

const replaceIdentityToken = async (newIdentityToken) => {
    // load the current localstorage object
    const userLocalStorage = h.getStore(STORE_NAME.USER);
    // replace the old one with the new one
    userLocalStorage.identityToken = newIdentityToken;
    set(STORE_NAME.USER, userLocalStorage);
};

export const switchProfileMutationFn = async (profile: IProfileData) => {
    try {
        const { profileGuid, profilePassword } = profile;
        const { apiKey, authToken, identityToken } = getUserInfo();

        const result = await container
            .resolve<IProfileService>(DI.SERVICES.IProfileService)
            .switchProfile(apiKey, profileGuid, profile, profilePassword, authToken, identityToken);
        return { result, profile };
    } catch (error) {
        redirectToSelectProfileOnSwitchError();
        // Prefer the API error payload; fall back to the original error if data is missing.
        throw error?.data ?? error;
    }
};

export const LoadProfile = createAsyncThunk<void, LoadProfileParams>(
    'load/profile',
    async ({ params: profileData, navigation }, ThunkAPI) => {
        // get new identityToken on user profile switch (choose)
        try {
            const { profileGuid, profilePassword } = profileData;
            const { apiKey, authToken, username, identityToken, authSign } = user.getUserInfo() || {};
            const result = await container
                .resolve<IProfileService>(DI.SERVICES.IProfileService)
                .switchProfile(apiKey, profileGuid, profileData, profilePassword, authToken, identityToken);

            if (result.error != undefined) {
                ThunkAPI.dispatch(verifyPasswordFailed(result.error));
                redirectToSelectProfileOnSwitchError();
                return result.error;
            } else {
                replaceIdentityToken(result.identityToken);

                // For kids profiles, ensure showWelcomeScreen is true regardless of server response
                // This ensures the gamification welcome modal appears for all kids profiles
                const shouldShowWelcomeScreen = profileData.kidsMode ? true : result?.showWelcomeScreen || false;

                const updatedProfile = {
                    ...profileData,
                    date: Date.now(),
                    showWelcomeScreen: shouldShowWelcomeScreen,
                    liveLikeAccessToken: result?.liveLikeAccessToken || null,
                    liveLikeProfileId: result?.liveLikeProfileId || null,
                };
                ThunkAPI.dispatch(selectProfileDone(updatedProfile));
                h.setStore(STORE_NAME.SELECTED_PROFILE, updatedProfile);

                // reloading miniEPG when user switch the profile and only in if miniEpg channels present in the store.
                const { miniEpg } = <any>ThunkAPI.getState();

                const miniEpgChannels = miniEpg.userProfileChannels;
                const errMess = miniEpg.errMess;
                if ((miniEpgChannels && miniEpgChannels.length) || errMess !== null) {
                    const miniEpgDataStream = miniEpg.data.data_stream;
                    const miniEpgDataParams = miniEpg.data.params;
                    ThunkAPI.dispatch(
                        fetchUserProfileMiniEPGChannelsList({
                            miniEpgDataStream: miniEpgDataStream ? miniEpgDataStream : apiPaths.MINIEPG_CHANNELS,
                            miniEpgDataParams: miniEpgDataParams ? miniEpgDataParams : PARAMS,
                        })
                    );
                }
                if (!profileData?.fromInit) {
                    const { urlToOpenTheProfile } = useProfileSwitching(profileData);
                    navigation(urlToOpenTheProfile);
                }

                return profileData;
            }
        } catch (error) {
            redirectToSelectProfileOnSwitchError();
            throw error.message;
        }
    }
);

export const LoadProfilesList = createAsyncThunk('load/profileList', async (ignoreLocalProfiles: boolean, ThunkAPI) => {
    try {
        const profiles = ignoreLocalProfiles ? await refreshProfileListQuery() : getProfileListFromCache();
        if (profiles) {
            ThunkAPI.dispatch(loadProfileDone(profiles));
        }
    } catch (error) {
        return ThunkAPI.rejectWithValue(error.data);
    }
});

export const addProfile = createAsyncThunk('add/profile', async (params: any, ThunkAPI) => {
    const { profileName, profileSettings, kidsMode } = params;
    const { apiKey, authToken, identityToken, authSign } = user.getUserInfo() || {};
    try {
        const addUserProfile = await container
            .resolve<IProfileService>(DI.SERVICES.IProfileService)
            .addProfile(apiKey, profileName, profileSettings, kidsMode, authToken, authSign, identityToken);
        ThunkAPI.dispatch(fetchUserRevision());
        ThunkAPI.dispatch(LoadProfilesList(true));
        return addUserProfile;
    } catch (error) {
        toast(__(`err_msg_${error?.data?.code}`), 'error');
        return ThunkAPI.rejectWithValue(error.data);
    }
});

export const updateProfile = createAsyncThunk<void, LoadProfileParams>(
    'update/profile',
    async ({ params, navigation }, ThunkAPI) => {
        try {
            const { profileGuid, userPassword, profileData, kidsMode, currentProfile } = params;
            const { profileName, profileSettings } = profileData;
            const { apiKey, authToken, identityToken, authSign } = user.getUserInfo() || {};
            const updateUserProfile = await container
                .resolve<IProfileService>(DI.SERVICES.IProfileService)
                .updateProfile(
                    apiKey,
                    profileGuid,
                    userPassword,
                    profileName,
                    profileSettings,
                    kidsMode,
                    authToken,
                    authSign,
                    identityToken,
                    currentProfile
                );

            if ('identityToken' in updateUserProfile) {
                replaceIdentityToken(updateUserProfile.identityToken);
            }
            ThunkAPI.dispatch(fetchUserRevision());
            ThunkAPI.dispatch(LoadProfilesList(true));
            const { profile } = <any>ThunkAPI.getState();
            const { selectedProfile } = profile;
            if (profileGuid === selectedProfile.profileGuid) {
                updateUserProfile.parentalControlValues = h.setUserRatingValues(
                    updateUserProfile.newParentalControl || updateUserProfile.parentalControl
                );
                ThunkAPI.dispatch(LoadProfile({ params: updateUserProfile, navigation }));
            }
            return updateUserProfile;
        } catch (error) {
            return ThunkAPI.rejectWithValue(error?.data);
        }
    }
);

export const removeProfile = createAsyncThunk('remove/profile', async (params: any, ThunkAPI) => {
    let { userPassword } = params;
    const { profile } = params;
    const { profileGuid } = profile;
    userPassword = userPassword ? userPassword : '';

    try {
        const { apiKey, authToken, authSign, identityToken } = user.getUserInfo() || {};
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        await profileService.deleteProfile(
            apiKey,
            profileGuid,
            userPassword,
            authToken,
            authSign,
            identityToken,
            profile
        );
        const profileData = <any>ThunkAPI.getState();
        const { profileList } = profileData;
        const deleteProfile = _.find(profileList.profiles, (a) => a.profileGuid === profileGuid);
        ThunkAPI.dispatch(fetchUserRevision());
        ThunkAPI.dispatch(LoadProfilesList(true));
        return deleteProfile;
    } catch (error) {
        return ThunkAPI.rejectWithValue(error.data);
    }
});

export const verifyPasswordOnSwitch = createAsyncThunk('verify/password', async (params: any, ThunkAPI) => {
    const { password, profileData } = params;
    const { profileGuid } = profileData;
    // get new identityToken on user profile switch (choose)
    try {
        const { apiKey, authToken, username, identityToken } = user.getUserInfo() || {};
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        const result = await profileService.switchProfile(
            apiKey,
            profileGuid,
            profileData,
            password,
            authToken,
            identityToken
        );

        if (result.error != undefined) {
            redirectToSelectProfileOnSwitchError();
            return result.error;
        } else {
            replaceIdentityToken(result?.identityToken);

            // For kids profiles, ensure showWelcomeScreen is true regardless of server response
            // This ensures the gamification welcome modal appears for all kids profiles
            const shouldShowWelcomeScreen = profileData.kidsMode ? true : result?.showWelcomeScreen || false;

            const updatedProfile = {
                ...profileData,
                date: Date.now(),
                profilePassword: password,
                showWelcomeScreen: shouldShowWelcomeScreen,
                liveLikeAccessToken: result?.liveLikeAccessToken || null,
                liveLikeProfileId: result?.liveLikeProfileId || null,
            };

            ThunkAPI.dispatch(selectProfileDone(updatedProfile));
            h.setStore(STORE_NAME.SELECTED_PROFILE, updatedProfile);
            const { urlToOpenTheProfile } = useProfileSwitching(profileData);
            window.location.href = urlToOpenTheProfile; // TODO This needs to be improved, as it reloading entire page.

            return profileData;
        }
    } catch (err) {
        redirectToSelectProfileOnSwitchError();
        return ThunkAPI.rejectWithValue(err.data);
    }
});

export const verifyPassword = createAsyncThunk('veify/password', async (params: any, ThunkAPI) => {
    const { password } = params;
    const userInfo = user.getUserInfo();
    const authToken = userInfo?.authToken;
    const username = userInfo?.username;
    const identityToken = user.getIdentityToken();
    const epochMs = Date.now().toString();
    const encPw = btoa(`${epochMs}:${password}`);
    const apiKey = userInfo?.apiKey;
    try {
        const result = await container
            .resolve<IProfileService>(DI.SERVICES.IProfileService)
            .validatePassword(authToken, apiKey, identityToken, epochMs, encPw, username);
        // verify success
        if (result && result.status === 'accepted') {
            return result;
        }

        return false;
    } catch (error) {
        return ThunkAPI.rejectWithValue(error.data);
    }
});

const profileSlice = createSlice({
    name: 'Profile',
    initialState: initialState.profile,
    reducers: {
        profileInitialize: (state, action) => {
            state.profileList = action.payload.list;
            state.selectedProfile = action.payload.selected;
            return state;
        },
        verifyPasswordFailed: (state, action) => {
            state.passwordVerifying.isLoading = false;
            state.passwordVerifying.errMess = action.payload.message;
            state.passwordVerifying.errCode = action.payload.errCode;
            return state;
        },
        clearVerifyPasswordErrors: (state) => {
            state.passwordVerifying.errCode = null;
            state.passwordVerifying.errMess = null;
            return state;
        },
        ClearDispatchedActions: (state) => {
            state.dispatchedActions.action = null;
            state.dispatchedActions.message = null;
            return state;
        },
        loadProfileDone: (state, action) => {
            state.profileList = action.payload;
            return state;
        },
        selectProfileDone: (state, action) => {
            state.selectedProfile = action.payload;
            return state;
        },
        closeSecondaryProductBanner: (state) => {
            state.isSecondaryProductOpen = false;
        },
        editProfileDone: (state) => {
            state.isFormSubmitted = false;
        },
    },
    extraReducers: (builder: any) => {
        // load profile cases
        builder.addCase(LoadProfile.pending, (state, action) => {
            state.isLoading = true;
            return state;
        });
        builder.addCase(LoadProfile.fulfilled, (state, action) => {
            state.selectedProfile = action.payload;
            state.isLoading = false;
            return state;
        });
        builder.addCase(LoadProfile.rejected, (state, action) => {
            state.errMess = action.error.message;
            state.selectedProfile = {};
            state.isLoading = false;
            return state;
        });

        // load profile list cases
        builder.addCase(LoadProfilesList.pending, (state, action) => {
            state.isLoading = true;
            return state;
        });
        builder.addCase(LoadProfilesList.fulfilled, (state, action) => {
            state.isLoading = false;
            return state;
        });
        builder.addCase(LoadProfilesList.rejected, (state, action) => {
            state.isLoading = false;
            return state;
        });

        // add profile cases
        builder.addCase(addProfile.pending, (state, action) => {
            state.isLoading = true;
            return state;
        });
        builder.addCase(addProfile.fulfilled, (state, action) => {
            state.isLoading = false;
            state.errMess = false;
            state.errCode = false;
            state.profile = action.payload;
            state.isFormSubmitted = true;
            state.dispatchedActions = { action: 'added', message: { profileName: action.payload.profileName } };
            return state;
        });
        builder.addCase(addProfile.rejected, (state, action) => {
            state.errCode = action.error.code;
            state.errMess = action.error.message;
            state.isFormSubmitted = false;
            state.isLoading = false;
            return state;
        });

        // update profile cases
        builder.addCase(updateProfile.pending, (state) => {
            state.isLoading = true;
            state.errMess = false;
            state.errCode = false;
            return state;
        });
        builder.addCase(updateProfile.fulfilled, (state, action) => {
            state.isLoading = false;
            state.errMess = false;
            state.errCode = false;
            state.profile = action.payload;
            state.isFormSubmitted = true;
            return state;
        });
        builder.addCase(updateProfile.rejected, (state, action) => {
            state.isLoading = false;
            state.errMess = action?.payload?.message;
            state.errCode = action?.payload?.code;
            state.isFormSubmitted = true;
            return state;
        });

        // removeProfile profile cases
        builder.addCase(removeProfile.pending, (state, action) => {
            state.isLoading = true;
            state.errMess = false;
            state.errCode = false;
            return state;
        });
        builder.addCase(removeProfile.fulfilled, (state, action) => {
            state.isLoading = false;
            state.errMess = false;
            state.errCode = false;
            state.profile = action.payload;
            state.isFormSubmitted = true;
            state.dispatchedActions = { action: 'deleted', message: { profileName: action.payload.profileName } };
            return state;
        });
        builder.addCase(removeProfile.rejected, (state, action) => {
            state.isLoading = false;
            state.errMess = action?.payload?.message;
            state.errCode = action?.payload?.code;
            state.isFormSubmitted = true;
            return state;
        });

        // verifyPasswordOnSwitch cases
        builder.addCase(verifyPasswordOnSwitch.pending, (state, action) => {
            state.passwordVerifying.isLoading = true;
            state.passwordVerifying.errMess = false;
            state.passwordVerifying.errCode = false;
            return state;
        });
        builder.addCase(verifyPasswordOnSwitch.fulfilled, (state, action) => {
            state.passwordVerifying.isLoading = false;
            state.passwordVerifying.errMess = null;
            state.passwordVerifying.errCode = null;
            return state;
        });
        builder.addCase(verifyPasswordOnSwitch.rejected, (state, action) => {
            state.passwordVerifying.isLoading = false;
            state.passwordVerifying.errMess = action.payload.message;
            state.passwordVerifying.errCode = action.payload.code;
            return state;
        });

        // verify password cases
        builder.addCase(verifyPassword.pending, (state, action) => {
            state.passwordVerifying.isLoading = true;
            state.passwordVerifying.errMess = false;
            state.passwordVerifying.errCode = false;
            return state;
        });
        builder.addCase(verifyPassword.fulfilled, (state, action) => {
            state.passwordVerifying.isLoading = false;
            state.passwordVerifying.errMess = null;
            state.passwordVerifying.errCode = null;
            return state;
        });
        builder.addCase(verifyPassword.rejected, (state, action) => {
            state.passwordVerifying.isLoading = false;
            state.passwordVerifying.errMess = action.payload.message;
            state.passwordVerifying.errCode = action.payload.code;
            return state;
        });
    },
});

export const {
    verifyPasswordFailed,
    profileInitialize,
    clearVerifyPasswordErrors,
    ClearDispatchedActions,
    loadProfileDone,
    selectProfileDone,
    closeSecondaryProductBanner,
    editProfileDone,
} = profileSlice.actions;

export default profileSlice.reducer;
